import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { KeyboardDismissWrapper } from '@/components/common/KeyboardDismissWrapper';
import { SVG_COMPONENTS, SVG_DIMENSIONS } from '@/assets/admin';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';

type ButtonType = 'add' | 'download';

interface TitleWithActionsProps {
  title: string;
  showAddButton?: boolean;
  showSelector?: boolean;
  showControlBar?: boolean;
  onAddPress?: () => void;
  buttonType?: ButtonType;
}

export const TitleWithActions: React.FC<TitleWithActionsProps> = ({
  title,
  showAddButton = true,
  onAddPress,
  buttonType = 'add',
}) => {
  const styles = useStyles();

  return (
    <KeyboardDismissWrapper style={styles.container}>
      <View style={styles.top}>
        <Text style={styles.titleText}>{title}</Text>
        {showAddButton && (
          <View style={styles.btns}>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={onAddPress}
              activeOpacity={0.8}
              accessibilityLabel={buttonType === 'add' ? 'Add new item' : 'Download'}
              accessibilityHint={buttonType === 'add' ? 'Tap to create a new item' : 'Tap to download'}
              accessibilityRole="button"
            >
              {buttonType === 'add' ? (
                <SVG_COMPONENTS.ADD_BUTTON {...SVG_DIMENSIONS.ADD_BUTTON} />
              ) : (
                <SVG_COMPONENTS.DOWNLOAD {...SVG_DIMENSIONS.DOWNLOAD} />
              )}
            </TouchableOpacity>
          </View>
        )}
      </View>
    </KeyboardDismissWrapper>
  );
};

const useStyles = () =>
  useThemedStyles(({ components }) => {
    const admin = components.admin;
    return StyleSheet.create({
      container: {
        alignSelf: 'stretch',
        height: 34,
        gap: 16,
      },
      top: {
        alignSelf: 'stretch',
        height: 34,
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 0,
      },
      titleText: {
        flex: 1,
        height: 33,
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: '600',
        fontSize: 22,
        lineHeight: 33,
        letterSpacing: 0,
        color: admin.dataTable.text,
        marginLeft: 0,
        marginRight: 20,
      },
      btns: {
        width: 34,
        height: 34,
        justifyContent: 'center',
        alignItems: 'center',
      },
      actionButton: {
        width: 34,
        height: 34,
        paddingHorizontal: 0,
        paddingVertical: 0,
        borderRadius: 8,
        // Brand pink CTA — preserved as a deliberate brand moment, same as
        // DownloadButton. Resio's add/download icons read against pink across
        // both themes.
        backgroundColor: PRIMITIVE_COLORS.resioPink,
        justifyContent: 'center',
        alignItems: 'center',
      },
    });
  });
